import { useEffect, useState } from "react";
import { ArrowDownLeft, ArrowUpRight, ArrowLeftRight, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { todayISO, dEN } from "@/lib/format";
import { Field, MoneyInput, BigButton, ImageUpload } from "@/components/admin/common";
import { AccountSelect } from "@/components/finance/shared";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/admin/DateInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const KINDS = [
  ["masuk", "Uang Masuk", ArrowDownLeft, "bg-emerald-600"],
  ["keluar", "Uang Keluar", ArrowUpRight, "bg-rose-600"],
  ["pindah", "Pindah Dana", ArrowLeftRight, "bg-sky-600"],
];
const ORDER = { masuk: ["pendapatan", "liabilitas", "ekuitas", "aset", "beban"], keluar: ["beban", "aset", "liabilitas", "ekuitas", "pendapatan"] };
const blank = (cash = "") => ({ kind: "keluar", amount: "", cash_account: cash, category_account: "", date: todayISO(), description: "",
  counterparty: "", booking_id: null, receipt_url: "", dpp: "", ppn: "", pph: "", npwp: "" });

export default function EntryForm({ accounts, taxMode, initial, onSaved }) {
  const [f, setF] = useState(initial || blank());
  const [meta, setMeta] = useState({ common: {} });
  const [bookings, setBookings] = useState([]);
  const [showTax, setShowTax] = useState(taxMode === "regular");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/finance/last-used").then((r) => {
      setMeta(r.data);
      if (!initial) setF((p) => ({ ...p, cash_account: p.cash_account || r.data.cash_account || "1-1200" }));
    });
    api.get("/admin/bookings").then((r) => setBookings(r.data));
  }, [initial]);
  useEffect(() => { setShowTax(taxMode === "regular"); }, [taxMode]);

  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const cash = accounts.filter((a) => a.is_cash);
  const cats = f.kind === "pindah" ? cash.filter((a) => a.code !== f.cash_account)
    : accounts.filter((a) => !a.is_cash).sort((a, b) => ORDER[f.kind].indexOf(a.type) - ORDER[f.kind].indexOf(b.type) || a.code.localeCompare(b.code));

  const save = async (e) => {
    e.preventDefault();
    if (!f.amount) return toast.error("Isi jumlah dulu");
    if (!f.category_account) return toast.error(f.kind === "pindah" ? "Pilih akun tujuan" : "Pilih kategori");
    setBusy(true);
    const body = { ...f, dpp: Number(f.dpp) || 0, ppn: Number(f.ppn) || 0, pph: Number(f.pph) || 0 };
    try {
      if (initial?.id) await api.put(`/admin/finance/transactions/${initial.id}`, body);
      else await api.post("/admin/finance/transactions", body);
      toast.success("Transaksi tersimpan");
      if (!initial) setF({ ...blank(f.cash_account), kind: f.kind, date: f.date });
      onSaved?.();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-5" data-testid="finance-entry-form">
      <div className="grid grid-cols-3 gap-2">
        {KINDS.map(([k, label, Icon, color]) => (
          <button type="button" key={k} onClick={() => setF((p) => ({ ...p, kind: k, category_account: "" }))} data-testid={`entry-kind-${k}`}
            className={`h-16 rounded-2xl flex flex-col items-center justify-center gap-1 text-sm font-bold border ${f.kind === k ? `${color} text-white border-transparent` : "bg-white border-border text-slate-600"}`}>
            <Icon className="w-5 h-5" />{label}
          </button>
        ))}
      </div>
      <Field label="Jumlah"><MoneyInput value={f.amount} onChange={set("amount")} testid="entry-amount-input" className="!h-14 !text-2xl font-extrabold" placeholder="0" /></Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={f.kind === "masuk" ? "Masuk ke akun Kas/Bank" : "Dari akun Kas/Bank"}>
          <AccountSelect accounts={cash} value={f.cash_account} onChange={set("cash_account")} grouped={false} testid="entry-cash-select" />
        </Field>
        <Field label={f.kind === "pindah" ? "Ke akun Kas/Bank" : "Kategori (Daftar Akun)"}>
          <AccountSelect accounts={cats} value={f.category_account} onChange={set("category_account")} common={meta.common?.[f.kind] || []}
            grouped={f.kind !== "pindah"} testid="entry-category-select" placeholder={f.kind === "pindah" ? "Pilih akun tujuan" : "Pilih kategori"} />
        </Field>
        <Field label="Tanggal"><DateInput required value={f.date} onChange={set("date")} testid="entry-date-input" /></Field>
        <Field label="Pihak terkait (opsional)"><Input value={f.counterparty} onChange={set("counterparty")} className="h-12" placeholder="Nama tamu / vendor" data-testid="entry-counterparty-input" /></Field>
        <Field label="Keterangan" className="sm:col-span-2"><Input value={f.description} onChange={set("description")} className="h-12" placeholder="mis. Beli solar speedboat" data-testid="entry-description-input" /></Field>
        <Field label="Hubungkan ke booking (opsional)">
          <Select value={f.booking_id || "none"} onValueChange={(v) => set("booking_id")(v === "none" ? null : v)}>
            <SelectTrigger className="h-12" data-testid="entry-booking-select"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="none">Tidak ada</SelectItem>
              {bookings.map((b) => <SelectItem key={b.id} value={b.id}>{b.code} · {b.customer_name} · {dEN(b.start_date)}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Bukti (foto nota / struk)"><ImageUpload value={f.receipt_url} onChange={set("receipt_url")} isPublic={false} testid="entry-receipt-upload" /></Field>
      </div>
      <button type="button" onClick={() => setShowTax(!showTax)} className="flex items-center gap-1 text-sm font-semibold text-oceandeep" data-testid="entry-tax-toggle">
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showTax ? "rotate-180" : ""}`} />Detail pajak {taxMode === "umkm" ? "(opsional — mode UMKM)" : ""}
      </button>
      {showTax && (
        <div className="grid sm:grid-cols-4 gap-3 bg-sand rounded-2xl p-4" data-testid="entry-tax-fields">
          <Field label="DPP"><MoneyInput value={f.dpp} onChange={set("dpp")} testid="entry-dpp-input" placeholder="= jumlah" /></Field>
          <Field label="PPN"><MoneyInput value={f.ppn} onChange={set("ppn")} testid="entry-ppn-input" /></Field>
          <Field label="PPh dipotong"><MoneyInput value={f.pph} onChange={set("pph")} testid="entry-pph-input" /></Field>
          <Field label="NPWP lawan transaksi"><Input value={f.npwp} onChange={set("npwp")} className="h-12" data-testid="entry-npwp-input" /></Field>
        </div>
      )}
      <BigButton type="submit" disabled={busy} className="w-full !h-14 text-base" data-testid="entry-save-btn">{busy ? "Menyimpan…" : "Simpan Transaksi"}</BigButton>
    </form>
  );
}
